'use client';

import { Link } from '@/i18n/routing';
import { useMemo } from 'react';
import { Bikepark, Snowpark, Pumptrack } from '@/types';
import FavoriteButton from './FavoriteButton';
import { useFavorites } from '@/hooks/useFavorites';
import { useTranslations } from 'next-intl';
import { Info, Route, Heart, DollarSign, CableCar, Users, UtensilsCrossed } from 'lucide-react';

interface ParksListProps {
  parks: (Bikepark | Snowpark | Pumptrack)[];
  onParkClick: (park: Bikepark | Snowpark | Pumptrack) => void;
  onParkHover?: (park: Bikepark | Snowpark | Pumptrack) => void;
  onParkHoverEnd?: () => void;
  type?: 'bikepark' | 'snowpark' | 'pumptrack';
}

export default function ParksList({ parks, onParkClick, onParkHover, onParkHoverEnd, type = 'bikepark' }: ParksListProps) {
  const t = useTranslations('parksList');
  const { isFavorite } = useFavorites();

  const renderStars = (rating: number) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  // Sort parks: favorites first, then the rest
  const sortedParks = useMemo(() => {
    return [...parks].sort((a, b) => {
      const aIsFavorite = isFavorite(a.documentId, type);
      const bIsFavorite = isFavorite(b.documentId, type);

      // Favorites first
      if (aIsFavorite && !bIsFavorite) return -1;
      if (!aIsFavorite && bIsFavorite) return 1;

      // Keep original order for the rest
      return 0;
    });
  }, [parks, isFavorite, type]);

  if (parks.length === 0) {
    return (
      <div className="bp-empty">
        <p>{t('noParksFound')}</p>
        <p>{t('tryChangingCriteria')}</p>
      </div>
    );
  }

  return (
    <>
      {sortedParks.map((park) => (
        <div
          key={park.documentId}
          className="bp-item"
          onClick={() => onParkClick(park)}
          onMouseEnter={() => onParkHover?.(park)}
          onMouseLeave={() => onParkHoverEnd?.()}
          style={{ cursor: 'pointer' }}
        >
          <div className="bp-item-header">
            <h4 className="bp-item-title">{park.name}</h4>
            <div className="bp-item-actions">
              <FavoriteButton
                id={park.documentId}
                slug={park.slug}
                name={park.name}
                type={type}
                variant="compact"
              />
              <Link
                href={`/${type}/${park.slug}`}
                className="bp-park-action-btn bp-park-action-details"
                title={t('viewDetails')}
                onClick={(e) => e.stopPropagation()}
              >
                <span className="bp-park-action-icon"><Info size={14} strokeWidth={2} /></span>
                <span className="bp-park-action-label">{t('viewDetails')}</span>
              </Link>
            </div>
          </div>
          <div className="bp-meta">
            <span className="bp-stars">{renderStars(park.rating)}</span>
            <span>• {park.region}</span>
            {'trails' in park && park.trails && park.trails.length > 0 && (
              <span>• <Route size={12} strokeWidth={2} style={{display: 'inline-block', verticalAlign: 'middle'}} /> {t('trailsCount', { count: park.trails.length })}</span>
            )}
            {'trailsCount' in park && park.trailsCount && (
              <span>• <Route size={12} strokeWidth={2} style={{display: 'inline-block', verticalAlign: 'middle'}} /> {t('trailsCount', { count: park.trailsCount })}</span>
            )}
          </div>
          {park.blurb && (
            <div className="bp-item-blurb">
              {park.blurb}
            </div>
          )}
          {park.price !== undefined && park.price !== null && (
            <div className="bp-item-price">
              {park.price === 0 ? <><Heart size={14} strokeWidth={2} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '4px'}} /> {t('free')}</> : <><DollarSign size={14} strokeWidth={2} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '4px'}} /> {t('priceFrom', { price: park.price }).replace('PLN', 'zł')}</>}
            </div>
          )}
          <div className="bp-tags">
            {park.lift && <span className="bp-tag"><CableCar size={12} strokeWidth={2} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '4px'}} /> {t('liftTag')}</span>}
            {park.familyFriendly && <span className="bp-tag"><Users size={12} strokeWidth={2} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '4px'}} /> {t('familyTag')}</span>}
            {park.restaurant && <span className="bp-tag"><UtensilsCrossed size={12} strokeWidth={2} style={{display: 'inline-block', verticalAlign: 'middle', marginRight: '4px'}} /> {t('restaurantTag')}</span>}
          </div>
        </div>
      ))}
    </>
  );
}
